import {Vue, Watch, Component, Prop} from 'vue-property-decorator';
import selectRow, { normalizeId } from './table-selection';

@Component
export default class TableScrollMixin extends Vue {
  scrolledItems: any[] = [];

  lastScrollEvent: any = null;
  scrollStack: number[] = [];

  @Prop({default: 700})
  autoVirtualScrollAt: number;

  @Prop({default: 155})
  upperIncrement: number;

  @Prop({default: 400})
  lowerIncrement: number;
  
  @Prop({default: 20})
  showRows: number = 20;

  @Prop({default: 32})
  rowSize: number = 32;

  @Prop({default: false}) 
  fixedLayoutBugFix!: boolean;

  @Prop({default: 'DT_RowId'}) 
  rowKey!: string;

  scrolledRowsCache: any = null;
  scrolledItemsCache: any = null;
  
  rowsCache: any = null;

  rowsCacheUpper: number = Number.MAX_SAFE_INTEGER;
  rowsCacheLower: number = Number.MIN_SAFE_INTEGER;
  rowsIndexData: {[type: string]: number} = {}; 
  
  scrollTop: number = 0;
  currentRow: number = 0;

  currentRenderedRow: number = 0;
  isNeedToVTabToRerender: boolean = false;

  vTabRevision: number = Number.MIN_SAFE_INTEGER;
  oldVTabRevision!: number;
  
  readonly upperBackupRows: number = 5;
  readonly upperBackupFactor: number = 0;
  readonly lowerBackupRows: number = 25;
  readonly lowerBackupRowsFactor: number = 0;
  
  get upperUpdateThreshold() { 
    return this.currentRenderedRow - this.upperBackupRows + this.upperBackupFactor; 
  }
  get lowerUpdateThreshold() { 
    return this.currentRenderedRow + this.showRows + this.lowerBackupRows - this.lowerBackupRowsFactor; 
  }

  get upperThreshold() { 
    return Math.max(this.currentRow - this.upperBackupRows, 0);
  }
  get lowerThreshold() { 
    return Math.min(this.scrolledItems.length, this.currentRow + this.showRows + this.lowerBackupRows); 
  }

  isScrollReady: boolean = true;
  isScrollReadyTimeout: NodeJS.Timeout = null;

  selectedItems: any[] = [];

  rowsHeight: number = 0;
  get rowsCount() {
    return this.lowerThreshold  - this.upperThreshold; 
  }

  get mismatchRows() {
    let mismatchHeight = Math.max(this.rowsHeight - this.rowsCount * this.rowSize, 0);
    return Math.floor(mismatchHeight / this.rowSize);
  }

  get tableTreeLevels() {
    return this.$store.getters.tableQueryForCurrentTab.treeLevels;
  }

  get overflow() {
    return this.$refs.overflow as HTMLElement;
  } 
  
  isScrollVisible: boolean = false;

  onWindowResize() {
    window.requestAnimationFrame(() => {
      this.isScrollVisible = this.overflow.scrollHeight > this.overflow.clientHeight;
    });

    this.$nextTick(() => {
      this.bus.emit('datatable-resizer-redraw');
    });
  }

  mounted() {
    window.addEventListener('resize', this.onWindowResize);
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  scrollLeft: number = 0;

  @Watch('scrolledItems')
  onSelectedItemsChanged(value: any) {
    if(this.isVirtualScroll) {
      this.scrolledRowsCache = null;
      this.rowsCache = null;
      this.vTabRevision = Number.MIN_SAFE_INTEGER;
      this.currentRenderedRow = 0;
      this.scrollTop = 0;
      
      this.rowsIndexData = {};

      value.forEach((item: any, index: any) => {
        this.rowsIndexData[item[this.rowKey]] = index;
      })

      if(this.overflow) {
        this.$nextTick(() => {
          this.overflow.scrollTo({
            top: 0,
            //@ts-ignore 
            behavior: 'instant',
          });
        });
      }
    }
  }

  registerScrollEventHandlers() {
    if(!this.overflow) {
      return;
    }
    
    if(this.scrollStack[0] && this.scrollStack[0] !== this.overflow.scrollTop) {
      this.overflow.scrollTo({
        top: this.scrollTop ,
        //@ts-ignore 
        behavior: 'instant',
      });
    }

    let rows = document.getElementsByClassName('datatable-row__virtual-scroll');

    // @ts-ignore
    if(this.selectable) {
      for(let i in rows) {
        if(rows.hasOwnProperty(i)) {
          //@ts-ignore
          let id = rows[i].dataset.id;
          rows[i].classList.remove('datatable__row-selected');
          rows[i].classList.remove('focus');

          const escapedId = id.replace('"', '&quot;');
  
          let checkboxIcon = document.querySelector(`.v-input[data-id="${escapedId}"] i`);
          if(checkboxIcon) {
            checkboxIcon.classList.remove('fa-check-square');
            checkboxIcon.classList.add('fa-square');
          }
  
          let checkbox = document.querySelector(`.v-input[data-id="${escapedId}"]`);
          if(checkbox) {
            checkbox.removeEventListener('click', this.checkboxClickListener);
            checkbox.addEventListener('click', this.checkboxClickListener);

            checkbox.removeEventListener('dblclick', this.stopPropagnationListener);
            checkbox.addEventListener('dblclick', this.stopPropagnationListener);
          }
          
          rows[i].removeEventListener('contextmenu', this.rowContextMenuListener);
          rows[i].addEventListener('contextmenu', this.rowContextMenuListener);
  
          rows[i].removeEventListener('click', this.rowClickListener);
          rows[i].addEventListener('click', this.rowClickListener);

          rows[i].removeEventListener('dblclick', this.rowDoubleClickListener);
          rows[i].addEventListener('dblclick', this.rowDoubleClickListener);
        }
      }
    }    

    this.scrollCleanup(rows);

    this.updateRowsHeight();
  }

  scrollCleanup(rows?: HTMLCollectionOf<Element>) {
    rows = rows || document.getElementsByClassName('datatable-row__virtual-scroll');
    let rowsIndex: any = {};

    for(let i in rows) {
      if(rows.hasOwnProperty(i)) {
        //@ts-ignore
        const id = rows[i].dataset.id;

        if (!id) return;

        rowsIndex[id] = i;
        rows[i].classList.remove('datatable__row-selected');
        rows[i].classList.remove('focus');

        const escapedId = id.replace('"', '&quot;');

        let checkboxIcon = document.querySelector(`.v-input[data-id="${escapedId}"] i`);
        if(checkboxIcon) {
          checkboxIcon.classList.remove('fa-check-square');
          checkboxIcon.classList.add('fa-square');
        }
      }
    }

    let ids = Object.keys(rowsIndex);
    this.$store.state.table.selectedItems
      .filter((item: any) => ids.indexOf(item[this.rowKey]) !== -1)
      .forEach((item: any) => {
        let index = rowsIndex[item[this.rowKey]];
        rows[index].classList.add('datatable__row-selected');
        rows[index].classList.add('focus');
        
        const escapedId = item[this.rowKey].replace('"', '&quot;');

        let selectedCheckboxIcon = document.querySelector(`.v-input[data-id="${escapedId}"] i`);
        if(selectedCheckboxIcon) {
          selectedCheckboxIcon.classList.remove('fa-square');
          selectedCheckboxIcon.classList.add('fa-check-square');
        }
      });
  }
  
  @Watch('isVirtualScroll', {
    immediate: true
  })
  onIsVirtualScrollChanged(value: boolean, oldValue: boolean) {  
    if(value !== oldValue) {
      this.$nextTick(() => {       
        this.registerScrollEventHandlers();

        if(this.overflow) {
          if(this.overflowScrollListener) {
            this.overflow.removeEventListener('scroll', this.overflowScrollListener);
          }
          
          if(value) {
            this.overflow.addEventListener('scroll', this.overflowScrollListener);
          } 
        }
      });

      if(value) {
        this.scrollTop = 0;
        this.currentRow = 0;
        this.vTabRevision++;
      }

      // console.log('aaa');
      // this.$nextTick(() => {
      //   this.vTabRevision++;
      // });
      // this.vTabRevision++;
    }
  }
  
  
  get isVirtualScroll() {
    return this.scrolledItems.length > this.autoVirtualScrollAt;
  } 

  get isInBorders() {
    return this.upperThreshold > this.rowsCacheUpper && this.lowerThreshold < this.rowsCacheLower;
  }

  overflowScrollListener(e: any) {
    this.addToScrollStack(e.target.scrollTop);

    this.$nextTick(() => {
      this.currentRow = Math.floor(e.target.scrollTop / this.rowSize);
      this.scrollTop = this.scrollStack[1];

      if(this.currentRow < this.upperUpdateThreshold || (this.currentRow + this.showRows) > this.lowerUpdateThreshold) {
        if(this.isScrollReady) {
          this.vTabRevision++;

          this.isScrollReady = false;
          clearTimeout(this.isScrollReadyTimeout);
          this.isScrollReadyTimeout = setTimeout(() => {
            this.isScrollReady = true;
            
            if(this.lastScrollEvent) {
              let lastScrollEvent = this.lastScrollEvent;
              this.lastScrollEvent = null;
              this.overflowScrollListener(lastScrollEvent);
            }
          }, 100);
        } else {
          this.lastScrollEvent = e;
        }
      }
    });
  }

  rowDoubleClickListener(ev: any) {
    const item = this.getItemByTarget(ev.target);  
    const id = normalizeId(item[this.rowKey]);

    const normalizedItem = {
      ...item,
      [this.rowKey]: id,
      id: id
    };
    
    //@ts-ignore
    const grouping: string = this.grouping
    
    this.bus.emit('row-report', normalizedItem, grouping);
  }



  stopPropagnationListener(ev: any) { 
    ev.preventDefault();
    ev.stopPropagation();
  }

  get rowClickListener() { 
    return this.clickListener(true);
  }

  get checkboxClickListener() { 
    return this.clickListener(false);
  }

  clickListener(single: boolean)  {
    return (ev: any) => {
      ev.stopPropagation();
      
      let item = this.getItemByTarget(ev.target);  
      selectRow(ev, this, item, single);
    }
  };
  
  rowContextMenuListener(ev: Event) {
    ev.preventDefault();

    let item = this.getItemByTarget(ev.target);  
    selectRow(ev as MouseEvent, this,  item, true, true);
  }

  getItemByTarget(target: any) {
    while(target.nodeName !== 'TR') {
      target = target.parentElement;
    }

    let id = target.dataset.id;
    return this.scrolledItems[this.rowsIndexData[id]];
  }

  treeViewExpandListener(ev: MouseEvent) {
    ev.stopPropagation();
    let item = this.getItemByTarget(ev.target); 
    this.$emit('treeExpandClick', item);
  }

  updateRowsHeight() {
    let overflowHeight = this.overflow.scrollHeight;
    let rows = document.getElementsByClassName('datatable-row__filler');
    let fillerRowsHeight = 0;
    for(let i in rows) {
      if(rows.hasOwnProperty(i)) {
        fillerRowsHeight += rows[i].clientHeight;
      }
    }
    this.rowsHeight = overflowHeight - fillerRowsHeight;
  }

  modifyRows(rows: any[]) {
    const vtag = this.$createElement;

    let result = rows.slice(this.upperThreshold - this.rowsCacheUpper, this.lowerThreshold - this.rowsCacheUpper);

    if(this.currentRow % 2 === 0) {
      let trParity = vtag('tr', {
        class: {
          'datatable-row__parity': true
        },
        style: {
          'border': 'none',
          'height': `0px !important`
        },
      });

      result.unshift(trParity);
    }

    let trPrepend = vtag('tr', {
      class: {
        'datatable-row__filler': true
      },
      style: {
        'border': 'none',
        'height': `${this.upperThreshold * this.rowSize}px !important`
      },
    });

    let trAppend = vtag('tr', {
      class: {
        'datatable-row__filler': true
      },
      style: {
        'border': 'none',
        'height': `${(this.scrolledItems.length - this.lowerThreshold) * this.rowSize}px !important`
      },
    });
    
    result.unshift(trPrepend);

    if(this.fixedLayoutBugFix) {
      let fixedLayoutBugFixTr = vtag('tr', {
        class: {
          'datatable-row__bug-fix': true
        },
        style: {
          'border': 'none',
          'border-top': 'none !important',
          'height': `0px !important`
        },
      }, [ 
        vtag('td', {
          style: {
            'border': 'none',
            'height': '0px !important', 
            'padding': '0 !important'
          }
        })
      ]);

      result.unshift(fixedLayoutBugFixTr);
    }

    result.push(trAppend);
    
    return result; 
  }
  
  // fix scroll inertia bug on chrome
  addToScrollStack(value: number) {
    this.scrollStack.unshift(value);
    this.scrollStack = this.scrollStack.splice(0, 2);
  }
}
